import React from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import { SidebarSection, Text, SelectHookForm } from 'bench-internal-theme';
import type { LedgerAccount } from '../types';
import { FormGroup } from '../FormGroups';
import { GET_TARGET_LEDGER_MAPPINGS } from '../queries';
import useLedgerAccounts from '../useLedgerAccounts';
import useV2Query from 'utils/useV2Query';
import { useFormContext } from 'react-hook-form';

const TargetDetailText = styled.div`
  color: ${colors.cloud};
  font-size: 12px;
  font-weight: 450;
  line-height: 16px;
  margin-bottom: 16px;
`;

const ExtraBottomMarginLayout = styled.div.attrs((props: { marginBottom: string }) => props)`
  margin-bottom: ${props => props.marginBottom};
`;

type TargetLedgerMappingSidebarSectionProps = {
  isSubmitting: boolean;
  ledgerAccount: LedgerAccount;
};

const pBTargetCreditLedgerSubstrings = [
  'Member Contribution',
  'Additional Paid in Capital',
  'Loans from Shareholder',
  'Loans from Member',
  'Due to',
  'Due to/from',
  'Due to/(from)',
];

const pBTargetDebitLedgerSubstrings = [
  'Member Drawing',
  'Shareholder Distribution',
  'Loans to Shareholder',
  'Loans to Member',
  'Due from',
  'Due to/from',
  'Due (to)/from',
];

const TargetLedgerMappingSidebarSection = ({
  ledgerAccount,
  isSubmitting,
}: TargetLedgerMappingSidebarSectionProps) => {
  const { setValue } = useFormContext();
  const { ledgerAccounts } = useLedgerAccounts();

  const { loading } = useV2Query(GET_TARGET_LEDGER_MAPPINGS, {
    variables: {
      ledgerAccountId: ledgerAccount?.id,
    },
    skip: !ledgerAccount?.id,
    onCompleted: data => {
      const defaultCreditLedger = data?.ledgerAccount.targetLedgerMappings?.find(mapping => {
        return mapping.entryType === 'CREDIT';
      }).targetLedgerAccount;
      const defaultDebitLedger = data.ledgerAccount.targetLedgerMappings?.find(mapping => {
        return mapping.entryType === 'DEBIT';
      }).targetLedgerAccount;
      setValue('pBContributionLedger', {
        value: defaultCreditLedger?.id,
        label: defaultCreditLedger?.name,
      });
      setValue('pBDrawLedger', { value: defaultDebitLedger?.id, label: defaultDebitLedger?.name });
    },
  });

  const getPBTargetLedgerOptions = (
    targetNameExamples: string[],
  ): { value: string; label: string }[] => {
    return ledgerAccounts
      .filter(ledgerAccount => {
        return (
          targetNameExamples.some(substring =>
            ledgerAccount.name.toUpperCase().includes(substring.toUpperCase()),
          ) && ledgerAccount.active
        );
      })
      .map(ledgerAccount => {
        return { value: ledgerAccount.id, label: ledgerAccount.name };
      });
  };

  return (
    <ExtraBottomMarginLayout marginBottom="30px">
      <SidebarSection background={colors.shell}>
        <TargetDetailText>
          Please identify which target ledgers that these transactions, if marked as business,
          should automatically be added to. If no target ledgers are selected, the transactions will
          not be visible in the Bench app.
        </TargetDetailText>
        <ExtraBottomMarginLayout marginBottom="10px">
          <FormGroup>
            <Text color={colors.cloud} styleType="small" uppercase>
              For Contributions
            </Text>
            <SelectHookForm
              aria-label="Select contribution ledger"
              name="pBContributionLedger"
              isDisabled={isSubmitting || loading}
              options={getPBTargetLedgerOptions(pBTargetCreditLedgerSubstrings)}
              rules={{
                required: { value: true, message: 'You must select a contribution ledger.' },
              }}
            />
          </FormGroup>
        </ExtraBottomMarginLayout>
        <FormGroup>
          <Text color={colors.cloud} styleType="small" uppercase>
            For Draws
          </Text>
          <SelectHookForm
            aria-label="Select draw ledger"
            name="pBDrawLedger"
            isDisabled={isSubmitting || loading}
            options={getPBTargetLedgerOptions(pBTargetDebitLedgerSubstrings)}
            rules={{
              required: { value: true, message: 'You must select a draw ledger.' },
            }}
          />
        </FormGroup>
      </SidebarSection>
    </ExtraBottomMarginLayout>
  );
};
export default TargetLedgerMappingSidebarSection;
